<template>
  <div
    class="absolute text-white"
    :class="viewportRotationClasses"
  >
    <!-- Full-screen black when hibernated -->
    <div
      v-if="hibernate"
      class="absolute inset-0 z-10 bg-black"
      aria-hidden="true"
    />

    <template v-else>
      <!-- Background -->
      <div
        class="absolute inset-0 overflow-hidden"
        :class="useDefaultChrome ? 'background' : 'bg-black'"
      >
        <div
          v-if="backgroundLayerStyle"
          class="absolute inset-0 pointer-events-none"
          :style="backgroundLayerStyle"
          aria-hidden="true"
        />
        <template v-if="useDefaultChrome">
          <div class="absolute -top-1/4 -left-1/4 w-3/4 h-3/4 rounded-full bg-[#FF7A1A]/10 blur-[8vw] pointer-events-none" />
          <div class="absolute -bottom-1/4 -right-1/4 w-2/3 h-2/3 rounded-full bg-[#C2410C]/8 blur-[8vw] pointer-events-none" />
        </template>
        <div
          v-else
          class="absolute inset-0 bg-black/35 pointer-events-none"
          aria-hidden="true"
        />
      </div>

      <!-- Logo — top-center (larger when tenant branding is set) -->
      <div
        class="absolute top-[4vh] left-1/2 z-10 flex -translate-x-1/2 items-center justify-center"
        :class="useDefaultChrome ? 'opacity-35' : 'opacity-90 drop-shadow-[0_2px_16px_rgba(0,0,0,0.5)]'"
        :style="bufferLogoBoxStyle"
      >
        <img
          v-if="logoUrl"
          :src="logoUrl"
          :alt="logoAlt"
          class="max-h-full max-w-full h-auto w-auto object-contain"
        >
      </div>

      <!-- Centered content -->
      <div class="absolute inset-0 flex items-center justify-center px-[6vmin]">
        <div
          v-if="showClock"
          class="player-idle-clock-fit"
        >
          <div
            class="player-idle-clock-display whitespace-nowrap font-black tabular-nums tracking-tight text-white"
          >
            {{ [hours, mins, seconds].join(' : ') }}
          </div>
        </div>

        <slot v-else />
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { PLAYER_IDLE_LOGO_DISPLAY } from '@lucimark/shared/constants/tenant-branding'
import { computed, onBeforeUnmount, ref, toRefs, watch } from 'vue'
import { usePlayerBranding } from '@/composables/usePlayerBranding'
import { usePlayerViewportRotationClasses } from '@/composables/usePlayerViewportRotationClasses'
import { getLocalAsUtcInstant } from '@/helpers/getLocalAsUtcInstant'

const props = defineProps<{ showClock: boolean }>()
const { showClock } = toRefs(props)

const { viewportRotationClasses } = usePlayerViewportRotationClasses()
const { logoUrl, logoAlt, hasCustomLogo, useDefaultChrome, backgroundLayerStyle } = usePlayerBranding()

const bufferLogoBoxStyle = computed(() => (
  hasCustomLogo.value
    ? {
        maxWidth: PLAYER_IDLE_LOGO_DISPLAY.bufferMaxWidth,
        maxHeight: PLAYER_IDLE_LOGO_DISPLAY.bufferMaxHeight
      }
    : { height: '6vh' }
))

const hours = ref('00')
const mins = ref('00')
const seconds = ref('00')
const hibernate = ref(false)

function tick() {
  const zoned = getLocalAsUtcInstant().toZonedDateTimeISO('UTC')
  hours.value = String(zoned.hour).padStart(2, '0')
  mins.value = String(zoned.minute).padStart(2, '0')
  seconds.value = String(zoned.second).padStart(2, '0')
}

tick()
const tickInterval = setInterval(() => tick(), 100)

let timeout: ReturnType<typeof setTimeout> | null = null

watch(
  showClock,
  (showClock) => {
    hibernate.value = false
    if (timeout !== null) {
      clearTimeout(timeout)
      timeout = null
    }
    if (showClock) {
      timeout = setTimeout(() => {
        hibernate.value = true
      }, 60000)
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  clearInterval(tickInterval)
  if (timeout !== null) {
    clearTimeout(timeout)
  }
})
</script>
